import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react'

import './_preview.scss'
import { useTranslation } from 'react-i18next'
import { type InfiniteData, useQueryClient } from 'react-query'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import type SwiperCore from 'swiper'
import { Keyboard, Mousewheel, Virtual } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

import * as SvgIcons from 'assets/svg/sprite'
import { IconCaret, IconExpand } from 'assets/svg/sprite'
import { addToast } from 'components/Toast/Toast'
import AddToPlaylist from 'components/UI/Modal/AddToPlaylist/AddToPlaylist'
import StatsPopup from 'components/UI/StatsPopup/StatsPopup'
import { OptionsDropdown } from 'components-ref/molecules'
import { useModalContext } from 'context/modalContext'
import { usePreviewContext } from 'context/previewContext'
import { usePostActions } from 'helpers/helperHooks'
import { useDeletePost, usePinPost, usePostsFeed } from 'helpers/hooks'
import { getCachedBlob, prefetchImageToBlob } from 'helpers/mediaPrefetch'
import { enqueuePostView } from 'helpers/trackingViews'
import { emitter } from 'services/emitter/emitter'
import { EventsEmitter } from 'services/emitter/events'
import { useUserProfile } from 'store'
import { usePreviewStore } from 'store/previewStore'
import { type IFile } from 'types/interfaces/IFile'
import { type IInfinitePage, type IPost } from 'types/interfaces/ITypes'

import PreviewComments from './PreviewComments/PreviewComments'
import SinglePostPreview from './SinglePostPreview/SinglePostPreview'

import 'swiper/css/bundle'

interface MinimizeIconProps {
  post: IPost
  minimized: boolean
  showControls: boolean
  onClick: () => void
  outlineColor?: string
}

export const MinimizeIcon = ({
  minimized,
  showControls,
  onClick,
  outlineColor = '#FFF'
}: MinimizeIconProps) => (
  <div onClick={onClick} className='minimize__icon'>
    {minimized
      ? <IconExpand fill={outlineColor} />
      : showControls && <IconCaret color={outlineColor} />}
  </div>
)

const PREFETCH_AHEAD = 1
const VIRTUAL_BEFORE = 1
const VIRTUAL_AFTER = 1
const FETCH_WHEN_REMAINING_POSTS = 2

function clampIndex(idx: number, len: number): number {
  if (!Number.isFinite(idx) || len <= 0) return 0
  return Math.min(Math.max(idx, 0), len - 1)
}

function collectPhotoUrls(post?: IPost | null): string[] {
  if (!post) return []

  const urls: string[] = []
  const photos = Array.isArray((post as any)?.photos) ? (post as any).photos : []
  const previews = Array.isArray((post as any)?.photos_preview) ? (post as any).photos_preview : []

  photos.forEach((photo: any) => {
    const url = (photo?.url ?? '').trim()
    if (url) urls.push(url)
  })

  previews.forEach((photo: any) => {
    const url = (photo?.url ?? photo?.thumb_url ?? '').trim()
    if (url) urls.push(url)
  })

  return Array.from(new Set(urls))
}

function flattenInfiniteToPosts(maybe: any): IPost[] {
  const pages = maybe?.pages
  if (!Array.isArray(pages)) return []

  return pages
    .map((page: any) => page?.page?.data?.data)
    .flat()
    .filter(Boolean)
}

const Preview = () => {
  const [previewFeed, setPreviewFeed] = useState<IPost[]>([])
  const [showControls, setShowControls] = useState(true)
  const [isOptionsNavOpen, setIsOptionsNavOpen] = useState(false)
  const [allFiles, setAllFiles] = useState<IFile[]>([])
  const [isMediaZoomed, setIsMediaZoomed] = useState(false)

  const swiperRef = useRef<SwiperCore | null>(null)
  const didAlignOnHydrateRef = useRef(false)
  const lastFetchSigRef = useRef('')
  const viewTimerRef = useRef<number | null>(null)

  const userData = useUserProfile()
  const navigate = useNavigate()
  const modalContext = useModalContext()
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  const previewCtx = usePreviewContext()
  if (!previewCtx) return null

  const {
    userId,
    filterQuery,
    clearModal,
    handleMinimize,
    updateSelectedPost
  } = previewCtx

  const previewOpen = usePreviewStore(s => s.previewOpen)
  const selectedPost = usePreviewStore(s => s.state.selectedPost)
  const selectedSlide = usePreviewStore(s => s.state.selectedSlide)
  const minimized = usePreviewStore(s => s.minimized)
  const commentsActive = usePreviewStore(s => s.commentsActive)
  const selectedPostData = usePreviewStore(s => s.selectedPostData)
  const setSelectedPostData = usePreviewStore(s => s.setSelectedPostData)
  const setCommentsActive = usePreviewStore(s => s.setCommentsActive)
  const commentCountByPostId = usePreviewStore(s => s.commentCountByPostId)
  const query = usePreviewStore(s => s.state.query)

  const postId = selectedPostData?.id
  const hasPost = !!postId

  const openOptions = useCallback(() => {
    window.setTimeout(() => setIsOptionsNavOpen(true), 0)
  }, [])

  const role = useMemo(
    () => userData?.id === selectedPostData?.user?.id ? 'owner' : '',
    [userData?.id, selectedPostData?.user?.id]
  )

  const cachedInfinite = useMemo(() => {
    if (!query) return undefined
    return queryClient.getQueryData<InfiniteData<IInfinitePage>>(query as any)
  }, [queryClient, query])

  const cachedPosts = useMemo(
    () => flattenInfiniteToPosts(cachedInfinite),
    [cachedInfinite]
  )

  const didHydrateFromCacheRef = useRef(false)

  useEffect(() => {
    if (didHydrateFromCacheRef.current || !cachedPosts.length) return

    didHydrateFromCacheRef.current = true
    setPreviewFeed(cachedPosts)

    if (typeof selectedPost === 'number') {
      const post = cachedPosts[clampIndex(selectedPost, cachedPosts.length)]
      if (post?.id) setSelectedPostData(post)
    }
  }, [cachedPosts, selectedPost, setSelectedPostData])

  useEffect(() => {
    const swiper = swiperRef.current
    if (!swiper) return

    const enabled = !isMediaZoomed
    swiper.allowTouchMove = enabled
    swiper.allowSlideNext = enabled
    swiper.allowSlidePrev = enabled
  }, [isMediaZoomed])

  const slidesSource = useMemo<IPost[]>(() => {
    const source = previewFeed.length
      ? previewFeed
      : selectedPostData?.id
        ? [selectedPostData]
        : []

    return source.map(post => {
      const override = commentCountByPostId[String(post.id)]
      return override == null ? post : { ...post, comment_count: override }
    })
  }, [previewFeed, selectedPostData, commentCountByPostId])


useLayoutEffect(() => {
  if (!previewOpen) return

  const swiper = swiperRef.current

  if (
    !swiper ||
    swiper.destroyed ||
    typeof selectedPost !== 'number' ||
    !slidesSource.length
  ) {
    return
  }

  const targetIndex = selectedPostData?.id
    ? slidesSource.findIndex(
        post => post.id === selectedPostData.id
      )
    : -1

  const resolvedIndex =
    targetIndex >= 0
      ? targetIndex
      : clampIndex(selectedPost, slidesSource.length)

  if (swiper.activeIndex !== resolvedIndex) {
    swiper.slideTo(resolvedIndex, 0, false)
  }
}, [
  previewOpen,
  selectedPost,
  selectedPostData?.id,
  slidesSource
])

  useEffect(() => {
    if (!selectedPostData?.id || !selectedPostData.is_purchased) return

    setPreviewFeed(prev => {
      let changed = false

      const next = prev.map(feedPost => {
        if (feedPost.id !== selectedPostData.id) return feedPost

        changed = true

        return {
          ...feedPost,
          ...selectedPostData,
          is_purchased: true,
          photos_preview: selectedPostData.photos_preview ?? [],
          videos_preview: selectedPostData.videos_preview ?? []
        }
      })

      return changed ? next : prev
    })
  }, [selectedPostData])

  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    status
  } = usePostsFeed(userId, filterQuery)

  useEffect(() => {
    if (!data || isFetching) return

    const flat = flattenInfiniteToPosts(data)
    if (!flat.length) return

    setPreviewFeed(prev => {
      const prevIds = prev.map(post => post.id).join(',')
      const nextIds = flat.map(post => post.id).join(',')
      return prevIds !== nextIds ? flat : prev
    })

    if (typeof selectedPost === 'number') {
      const post = flat[clampIndex(selectedPost, flat.length)]
      if (post?.id) setSelectedPostData(post)
    }
  }, [data, isFetching, selectedPost, setSelectedPostData])



  const slidesLen = slidesSource.length
  const isUsingRealFeed = previewFeed.length > 0

  const initialSlide = useMemo(() => {
    if (!slidesLen) return 0

    if (selectedPostData?.id) {
      const index = slidesSource.findIndex(post => post.id === selectedPostData.id)
      if (index >= 0) return index
    }

    return clampIndex(
      typeof selectedPost === 'number' ? selectedPost : 0,
      slidesLen
    )
  }, [selectedPost, selectedPostData?.id, slidesSource, slidesLen])

  useEffect(() => {
    const core = swiperRef.current
    if (!core || !slidesLen) return

    requestAnimationFrame(() => {
      core.virtual?.update?.(true)
    })
  }, [slidesLen])

  useEffect(() => {
    if (!previewFeed.length || didAlignOnHydrateRef.current) return

    const core = swiperRef.current
    if (!core) return

    const target = selectedPostData?.id
      ? previewFeed.findIndex(post => post.id === selectedPostData.id)
      : selectedPost

    const safeTarget = clampIndex(
      typeof target === 'number' && target >= 0 ? target : 0,
      previewFeed.length
    )

    didAlignOnHydrateRef.current = true

    if (core.activeIndex !== safeTarget) {
      requestAnimationFrame(() => core.slideTo(safeTarget, 0))
    }
  }, [previewFeed, selectedPost, selectedPostData?.id])

  useEffect(() => {
    if (!selectedPostData) return

    emitter.emit(EventsEmitter.STOP_ALL_AUDIO_POSTS)

    if (!selectedPostData.is_purchased) {
      setAllFiles([])
      return
    }

    setAllFiles([
      ...(selectedPostData.photos ?? []),
      ...(selectedPostData.videos ?? []),
      ...(selectedPostData.sounds ?? [])
    ].sort((a: any, b: any) => (a?.order ?? 0) - (b?.order ?? 0)))
  }, [selectedPostData])

  useEffect(() => {
    if (!hasPost || !userData?.id || !postId) return

    const creatorId =
      selectedPostData?.user_id ??
      selectedPostData?.user?.id

    if (!creatorId || creatorId === userData.id) return

    if (viewTimerRef.current != null) {
      window.clearTimeout(viewTimerRef.current)
      viewTimerRef.current = null
    }

    viewTimerRef.current = window.setTimeout(() => {
      enqueuePostView(postId)
    }, 1000)

    return () => {
      if (viewTimerRef.current != null) {
        window.clearTimeout(viewTimerRef.current)
        viewTimerRef.current = null
      }
    }
  }, [
    hasPost,
    postId,
    userData?.id,
    selectedPostData?.user_id,
    selectedPostData?.user?.id
  ])

  const [activeIndex, setActiveIndex] = useState(
    typeof selectedPost === 'number' ? selectedPost : 0
  )

  useEffect(() => {
    if (typeof selectedPost === 'number') setActiveIndex(selectedPost)
  }, [selectedPost])

  const prefetchSignature = useMemo(() => {
    if (!slidesLen) return 'empty'

    const idx = clampIndex(activeIndex, slidesLen)
    const ids: Array<string | number> = []
    const current = slidesSource[idx]

    if (current?.id) ids.push(current.id)

    for (let step = 1; step <= PREFETCH_AHEAD; step++) {
      const next = slidesSource[idx + step]
      if (next?.id) ids.push(next.id)
    }

    return `${idx}:${ids.join(',')}`
  }, [slidesSource, slidesLen, activeIndex])

  useEffect(() => {
    if (!slidesLen) return

    const idx = clampIndex(activeIndex, slidesLen)
    const controller = new AbortController()
    const urls = new Set<string>()

    collectPhotoUrls(slidesSource[idx]).forEach(url => urls.add(url))

    for (let step = 1; step <= PREFETCH_AHEAD; step++) {
      const next = slidesSource[idx + step]
      if (next) collectPhotoUrls(next).forEach(url => urls.add(url))
    }

    urls.forEach(url => {
      if (!url || getCachedBlob(url)) return
      prefetchImageToBlob(url, controller.signal).catch(() => {})
    })

    return () => controller.abort()
  }, [prefetchSignature, slidesSource, slidesLen, activeIndex])

  const maybeFetchNext = useCallback(
    (nextActiveIndex: number) => {
      if (!fetchNextPage || !hasNextPage || isFetchingNextPage || !slidesLen) return

      const remaining = slidesLen - 1 - nextActiveIndex
      if (remaining > FETCH_WHEN_REMAINING_POSTS) return

      const sig = `${nextActiveIndex}:${slidesLen}`
      if (lastFetchSigRef.current === sig) return

      lastFetchSigRef.current = sig
      fetchNextPage()
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage, slidesLen]
  )

  const onMutateError = useCallback(
    (_err: any, context: { cachedData: InfiniteData<IInfinitePage> | undefined }) => {
      if (context?.cachedData) {
        queryClient.setQueryData(query as any, context.cachedData)
      }
    },
    [queryClient, query]
  )

  const { onPinPostMutate, onDeletePostMutate } = usePostActions(
    (postId ?? 0) as any,
    query as any
  )

  const { pin } = usePinPost({
    onMutate: async () => await onPinPostMutate(),
    onError: (e: any, context: { cachedData: InfiniteData<IInfinitePage> | undefined }) =>
      onMutateError(e, context)
  })

  const { deletePostMutation } = useDeletePost({
    onMutate: async () => await onDeletePostMutate(),
    onError: (
      e: any,
      _id: any,
      context: { cachedData: InfiniteData<IInfinitePage> | undefined }
    ) => onMutateError(e, context)
  })

  const openStatsModal = useCallback(() => {
    if (!selectedPostData) return

    modalContext.addModal(
      '',
      <StatsPopup
        statsData={{
          overallRevenue: selectedPostData.overall_revenue,
          purchasesCount: selectedPostData.purchases_count,
          purchasesSum: selectedPostData.purchases_sum,
          tipsCount: selectedPostData.tips_count,
          tipsSum: selectedPostData.tips_sum,
          conversionRate: selectedPostData.conversion_rate || 0,
          uniqueImpressions: selectedPostData.unique_impressions,
          uniqueViews: selectedPostData.unique_views,
          price: selectedPostData.price
        }}
        closeStats={() => modalContext.clearModal()}
      />,
      true,
      true,
      'previewStats'
    )
  }, [modalContext, selectedPostData])

  const optionsArray = useMemo(() => {
    if (!hasPost) return []

    if (role === 'owner') {
      return [
        {
          icon: <SvgIcons.IconOptionsEdit />,
          text: t('editPost'),
          disabled: false,
          action: () => {
            navigate(`/edit/post/${postId}`)
            if (isOptionsNavOpen) setIsOptionsNavOpen(false)
            modalContext.clearModal()
            clearModal()
          }
        },
        {
          icon: <SvgIcons.IconOptionsPin />,
          text: selectedPostData?.is_pinned ? t('unpinPost') : t('pinPost'),
          disabled: false,
          action: () => {
            if (hasPost) pin.mutate(postId)
          }
        },
        {
          icon: <SvgIcons.IconOptionsStats />,
          text: t('viewStats'),
          disabled: false,
          action: () => {
            openStatsModal()
            if (isOptionsNavOpen) setIsOptionsNavOpen(false)
          }
        }
      ]
    }

    return [
      {
        icon: <SvgIcons.IconOptionsPlaylist />,
        text: t('addToList'),
        disabled: allFiles.length === 0,
        action: () => {
          if (!hasPost) return

          modalContext.addModal(
            '',
            <AddToPlaylist
              itemId={postId}
              itemType='post'
              closeFn={() => modalContext.clearModal()}
            />,
            true,
            true
          )
        }
      },
      {
        icon: <SvgIcons.IconOptionsCopy />,
        text: t('copyLinkToPost'),
        disabled: false,
        action: () => {
          if (!hasPost) return

          navigator.clipboard
            .writeText(`${window.location.origin}/post/${postId}`)
            .then(() => {
              if (isOptionsNavOpen) setIsOptionsNavOpen(false)
              addToast('success', t('urlCopiedToClipboard'))
            })
        }
      }
    ]
  }, [
    hasPost,
    role,
    t,
    navigate,
    postId,
    selectedPostData?.is_pinned,
    isOptionsNavOpen,
    modalContext,
    clearModal,
    pin,
    openStatsModal,
    allFiles.length
  ])

  const onSlideChangeHideTour = useCallback(
    (core: SwiperCore) => {
      const nextIndex = core?.activeIndex ?? 0
      setActiveIndex(nextIndex)
      maybeFetchNext(nextIndex)

      if (!slidesLen) return

      const currentPost = slidesSource[nextIndex]
      if (currentPost?.id) setSelectedPostData(currentPost)
    },
    [slidesSource, slidesLen, maybeFetchNext, setSelectedPostData]
  )

  return (
    <div className='preview__wrapper'>
      {hasPost && isOptionsNavOpen && (
        <OptionsDropdown
          role={role}
          content='post'
          isOptionsNavOpen={isOptionsNavOpen}
          setIsOptionsNavOpen={setIsOptionsNavOpen}
          postId={postId}
          options={optionsArray}
          deleteFn={() => {
            if (!hasPost) return

            deletePostMutation.mutate(postId)
            setIsOptionsNavOpen(false)
            modalContext.clearModal()
            clearModal()
          }}
          withoutBottomNavigation
        />
      )}

      {typeof selectedPost === 'number' && slidesLen > 0 && (
        <Swiper
          allowTouchMove={!isMediaZoomed}
          modules={[Keyboard, Virtual, Mousewheel]}
          mousewheel={{
            enabled: true,
            forceToAxis: true,
            sensitivity: 0.5,
            releaseOnEdges: true
          }}
          virtual={{
            enabled: true,
            addSlidesBefore: VIRTUAL_BEFORE,
            addSlidesAfter: VIRTUAL_AFTER
          }}
          onInit={(core: SwiperCore) => {
            swiperRef.current = core
            core.el?.setAttribute('tabindex', '0')
            core.el?.focus()

            requestAnimationFrame(() => {
              core.virtual?.update?.(true)
            })
          }}
          initialSlide={initialSlide}
          style={{ width: '100%', height: '100%' }}
          className='mySwiper'
          longSwipes={false}
          longSwipesRatio={0.25}
          shortSwipes
          direction='vertical'
          speed={500}
          key={
            previewFeed.length
              ? 'preview-feed'
              : `preview-single-${selectedPostData?.id}`
          }
          keyboard={{ enabled: true, onlyInViewport: true }}
          touchStartPreventDefault
          passiveListeners={false}
          resistanceRatio={0.85}
          touchReleaseOnEdges
          slidesPerView={1}
          slidesPerGroup={1}
          centeredSlides
          pagination={false}
          onSlideChangeTransitionStart={() => {
            if (!showControls) setShowControls(true)
          }}
          onSlideChange={onSlideChangeHideTour}
          noSwiping={!showControls || commentsActive || minimized}
        >
          {slidesSource.map((post, idx) => (
            <SwiperSlide
              key={post?.id ?? idx}
              virtualIndex={idx}
            >
              <PostPreviewWrapper>
                <SinglePostPreview
                  post={post}
                  selectedSlide={
                    post?.id === selectedPostData?.id
                      ? selectedSlide ?? 0
                      : 0
                  }
                  showControls={showControls}
                  onZoomStateChange={setIsMediaZoomed}
                  setIsOptionsNavOpen={setIsOptionsNavOpen}
                  onOpenOptions={openOptions}
                  minimizeFn={(fileIndex: number, type: string) => {
                    updateSelectedPost(idx, fileIndex, type, post)
                    if (minimized && !showControls) setShowControls(true)
                    handleMinimize(!minimized)
                  }}
                  toggleControls={() => setShowControls(value => !value)}
                  isFirstPost={idx === 0}
                  hasNextPage={!!hasNextPage}
                  isInViewport={post?.id === selectedPostData?.id}
                  isLastPost={
                    isUsingRealFeed &&
                    idx === previewFeed.length - 1
                  }
                />
              </PostPreviewWrapper>
            </SwiperSlide>
          ))}
        </Swiper>
      )}

      {hasPost && selectedPostData && (
        <PreviewComments
          commentsActive={commentsActive}
          setCommentsActive={setCommentsActive}
          activeCommentsPostId={postId}
          commentCount={
            commentCountByPostId[postId] ??
            selectedPostData.comment_count
          }
          isMyPost={userData?.id === selectedPostData.user?.id}
        />
      )}
    </div>
  )
}

export default Preview

const PostPreviewWrapper = styled.div`
  height: 100%;
  min-height: 100%;
  max-height: 100%;
`